import { pdkAxios } from '@/lib/pdk-axios.ts';
import { db } from '@/models/db';
import type {
  DeltaPacketRecord,
  FullHydrateResponse,
} from '@/models/hydrate.ts';
import { ProtectionPlanTypeEntity } from '@/models/registry.ts';
import type {
  BaseStore,
  StoreHydrationDelegator,
} from '@/models/stores/base.ts';
import type { ProtectionPlanType as ProtectionPlanTypeType } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';

class ProtectionPlanTypeStore
  implements BaseStore<ProtectionPlanTypeEntity, ProtectionPlanTypeType>
{
  store: ObservableMap<string, ProtectionPlanTypeEntity> = observable.map();

  constructor() {
    makeAutoObservable(this, { store: observable.shallow }, { autoBind: true });
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ProtectionPlanTypeType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ProtectionPlanTypeType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.protection_plan_types.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): ProtectionPlanTypeEntity | undefined {
    return this.store.get(id);
  }

  add(input: ProtectionPlanTypeType) {
    const protectionPlanType = new ProtectionPlanTypeEntity(input);
    this.store.set(protectionPlanType.id, protectionPlanType);
  }

  save(protectionPlanType: ProtectionPlanTypeEntity) {
    this.store.delete(protectionPlanType.id);
    this.store.set(protectionPlanType.id, protectionPlanType);
  }

  upsert(protectionPlanType: ProtectionPlanTypeType) {
    const idToUse = protectionPlanType.id;
    const existingProtectionPlanType = this.getById(idToUse);
    if (existingProtectionPlanType) {
      this.replace({ id: idToUse, protectionPlanType });
    } else {
      this.add(protectionPlanType);
    }
  }

  replace({
    id,
    protectionPlanType,
  }: {
    id: string;
    protectionPlanType: ProtectionPlanTypeType;
  }) {
    const existingProtectionPlanType = this.getById(id);
    if (!existingProtectionPlanType) return;
    existingProtectionPlanType.type = protectionPlanType.type;

    this.save(existingProtectionPlanType);
  }
}

export const protectionPlanTypeStore = new ProtectionPlanTypeStore();

// TODO -- currently doing full hydrate every time, since protection plan types cant be tracked via conventional delta sync
class ProtectionPlanTypeStoreHydrationDelegator
  implements StoreHydrationDelegator<ProtectionPlanTypeType>
{
  async hydrate({
    fullHydrateEndpoint,
  }: {
    fullHydrateEndpoint: string;
    deltaSyncEndpoint: string | null;
  }) {
    const resp =
      await pdkAxios.get<FullHydrateResponse<ProtectionPlanTypeType>>(
        fullHydrateEndpoint,
      );
    for (const protectionPlanType of resp.data.data) {
      await db.protection_plan_types.put(
        protectionPlanType,
        protectionPlanType.id,
      );
      protectionPlanTypeStore.upsert(protectionPlanType);
    }
  }
}

export const protectionPlanTypeStoreHydrationDelegator =
  new ProtectionPlanTypeStoreHydrationDelegator();
